import { inject, Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import {
  catchError,
  map,
  Observable,
  of,
  switchMap,
  throwError,
  timeout,
} from 'rxjs';
import { ServerContextService } from '../../../../feature-vault/src/lib/servers/server-context.service';
import { vaultTenantHeaders } from '@nebula/shared-vault/data-access';
import {
  IngestProject,
  IngestProjectCreate,
  IngestProjectUpdate,
  IngestStatusResponse,
  IngestSubfolder,
  IngestSubfolderCreate,
  IngestRemoteFile,
  IngestUploadResponse,
  IngestDuplicateCheckResponse,
} from '../../../../domain/src/lib/ingest/ingest-project.model';
import { normalizeIngestDuplicateCheck } from './ingest-duplicate.util';
import {
  normalizeIngestProject,
  normalizeIngestProjectList,
} from './ingest-project-normalize.util';

@Injectable({ providedIn: 'root' })
export class IngestProjectService {
  private readonly http = inject(HttpClient);
  private readonly serverContext = inject(ServerContextService);

  /**
   * Rutas relativas: `createApiVaultInterceptor(API_VAULT)` antepone `environment.API_VAULT`
   * (API desplegada). No usa `API_BASE_URL` de Simappe.
   */
  private readonly ingestPath = '/api/ingest';

  private ingestUrl(): string {
    return this.ingestPath;
  }

  private projectsUrl(): string {
    return `${this.ingestUrl()}/projects`;
  }

  private tenantHeaders(): HttpHeaders {
    return vaultTenantHeaders(this.serverContext.getContext().tenantId);
  }

  /** Solo proyectos activos (selector de ingesta y carga). */
  listForIngest(): Observable<IngestProject[]> {
    return this.list().pipe(
      map((projects) => projects.filter((project) => project.is_active === true)),
    );
  }

  list(): Observable<IngestProject[]> {
    return this.http
      .get<unknown>(this.projectsUrl(), {
        headers: this.tenantHeaders(),
      })
      .pipe(
        timeout(30000),
        map((raw) => normalizeIngestProjectList(raw)),
      );
  }

  /**
   * El OpenAPI no define `GET /ingest/projects/{id}` (solo listado, PUT, DELETE).
   * Resolvemos el proyecto desde `GET /ingest/projects` y filtramos por `id`.
   */
  getById(id: number): Observable<IngestProject> {
    return this.list().pipe(
      switchMap((projects) => {
        const found = projects?.find(
          (p) => Number(p?.id) === Number(id),
        );
        if (!found) {
          return throwError(
            () =>
              new HttpErrorResponse({
                status: 404,
                statusText: 'Not Found',
                error: {
                  detail:
                    'Expediente no encontrado. Es posible que haya sido eliminado o que el listado esté desactualizado.',
                },
              }),
          );
        }
        return of(found);
      }),
    );
  }

  create(body: IngestProjectCreate): Observable<IngestProject> {
    return this.http
      .post<unknown>(this.projectsUrl(), body, {
        headers: this.tenantHeaders(),
      })
      .pipe(
        map((raw) => {
          const normalized = normalizeIngestProject(raw);
          if (normalized) return normalized;
          return {
            id: Number((raw as Record<string, unknown> | null)?.['id']) || 0,
            name: body.name,
            description: body.description ?? null,
            folder_path: body.folder_path?.trim() || body.name,
            is_active: body.is_active !== false,
          };
        }),
      );
  }

  update(id: number, body: IngestProjectUpdate): Observable<IngestProject> {
    return this.http
      .put<unknown>(`${this.projectsUrl()}/${id}`, body, {
        headers: this.tenantHeaders(),
      })
      .pipe(
        map((raw) => {
          const normalized = normalizeIngestProject(raw);
          if (normalized) return normalized;
          return {
            id,
            name: body.name ?? '',
            description: body.description ?? null,
            folder_path: body.folder_path?.trim() || body.name || '',
            is_active: body.is_active !== false,
          };
        }),
      );
  }

  delete(id: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.projectsUrl()}/${id}`, {
      headers: this.tenantHeaders(),
    });
  }

  getSubfolders(projectId: number): Observable<IngestSubfolder[]> {
    return this.http
      .get<IngestSubfolder[]>(
        `${this.projectsUrl()}/${projectId}/subfolders`,
        { headers: this.tenantHeaders() },
      )
      .pipe(timeout(30000));
  }

  createSubfolder(
    projectId: number,
    body: IngestSubfolderCreate,
  ): Observable<IngestSubfolder> {
    return this.http.post<IngestSubfolder>(
      `${this.projectsUrl()}/${projectId}/subfolders`,
      body,
      { headers: this.tenantHeaders() },
    );
  }

  /**
   * Un solo `POST` con varias partes `files` y opcionalmente `file_path_0`… (mismo contrato que el front React).
   * Timeout largo para lotes grandes (React usaba 2 h).
   */
  uploadIngest(formData: FormData): Observable<HttpEvent<IngestUploadResponse>> {
    return this.http
      .post<IngestUploadResponse>(`${this.ingestUrl()}/upload`, formData, {
        headers: this.tenantHeaders(),
        observe: 'events',
        reportProgress: true,
      })
      .pipe(timeout(7_200_000));
  }

  /** Pre-validación de duplicados antes de POST /upload (#0000044). */
  checkDuplicate(params: {
    projectId: number;
    filename: string;
    subfolderPath?: string | null;
  }): Observable<IngestDuplicateCheckResponse> {
    let httpParams = new HttpParams()
      .set('project_id', String(params.projectId))
      .set('filename', params.filename);
    const subfolder = params.subfolderPath?.trim();
    if (subfolder) {
      httpParams = httpParams.set('subfolder_path', subfolder);
    }
    return this.http
      .get<unknown>(`${this.ingestUrl()}/check-duplicate`, {
        headers: this.tenantHeaders(),
        params: httpParams,
      })
      .pipe(
        timeout(30000),
        map((raw) => normalizeIngestDuplicateCheck(raw)),
      );
  }

  getStatus(uploadId: string): Observable<IngestStatusResponse> {
    return this.http.get<IngestStatusResponse>(`${this.ingestUrl()}/status/${uploadId}`, {
      headers: this.tenantHeaders(),
    });
  }

  /**
   * Listado de archivos y subcarpetas en una ruta del proyecto.
   * Si el endpoint aún no existe en el backend, devuelve [] sin romper la UI.
   */
  listFiles(projectId: number, folderPath: string): Observable<IngestRemoteFile[]> {
    const params = new HttpParams().set('path', folderPath ?? '');
    return this.http
      .get<IngestRemoteFile[]>(`${this.projectsUrl()}/${projectId}/files`, {
        headers: this.tenantHeaders(),
        params,
      })
      .pipe(catchError(() => of([])));
  }
}